import { useState } from "react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { Check, ClipboardCopy } from "lucide-react"
import ReactMarkdown, { type Components } from "react-markdown"
import remarkGfm from "remark-gfm"

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="bg-muted/50 relative my-2 rounded-md border">
      <pre className="overflow-x-auto p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap">
        {code}
      </pre>
      <Button
        size="sm"
        variant="outline"
        className="absolute top-1.5 right-1.5 h-6 px-2"
        onClick={handleCopy}
      >
        {copied ? <Check className="h-3 w-3" /> : <ClipboardCopy className="h-3 w-3" />}
      </Button>
    </div>
  )
}

const markdownComponents: Components = {
  // Unwrap <pre> so our block <CodeBlock> (a div) isn't nested inside it.
  pre: ({ children }) => <>{children}</>,
  code: ({ className, children }) => {
    const isBlock = /language-/.test(className || "")
    const text = String(children).replace(/\n$/, "")
    if (isBlock) return <CodeBlock code={text} />
    return <code className="bg-muted rounded px-1 py-0.5 font-mono text-xs">{children}</code>
  },
  h2: ({ children }) => <h2 className="mt-2 mb-2 text-base font-semibold">{children}</h2>,
  h3: ({ children }) => <h3 className="mt-2 mb-1 text-sm font-semibold">{children}</h3>,
  p: ({ children }) => <p className="text-foreground mb-2 text-sm leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="mb-2 ml-4 list-disc space-y-1 text-sm">{children}</ul>,
  ol: ({ children }) => <ol className="mb-2 ml-4 list-decimal space-y-1 text-sm">{children}</ol>,
  li: ({ children }) => <li className="text-foreground text-sm leading-relaxed">{children}</li>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noreferrer" className="text-primary underline">
      {children}
    </a>
  ),
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
}

/**
 * Renders trusted Markdown (team-authored tutorials). Raw HTML is not enabled.
 */
export function Markdown({ children, className }: { children: string; className?: string }) {
  return (
    <div className={cn("text-sm", className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {children}
      </ReactMarkdown>
    </div>
  )
}
